/**
 * useNotifications — Firestore onSnapshot for user notifications
 *
 * Subscribes to `users/{uid}/notifications` where `read == false`,
 * ordered by createdAt descending, limit 20.
 *
 * Returns:
 * - notifications: array of notification objects
 * - unreadCount: number of unread notifications
 * - markAsRead(id): mark a single notification as read
 * - markAllRead(): mark all unread notifications as read
 */
import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../utils/firebase";

export function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userId) return;

    const notifRef = collection(db, `users/${userId}/notifications`);
    const q = query(
      notifRef,
      where("read", "==", false),
      orderBy("createdAt", "desc"),
      limit(20)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setNotifications(items);
        setUnreadCount(items.length);
      },
      (error) => {
        console.error("[useNotifications] Snapshot error:", error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const markAsRead = useCallback(
    async (notificationId) => {
      if (!userId || !notificationId) return;
      try {
        const notifDoc = doc(db, `users/${userId}/notifications`, notificationId);
        await updateDoc(notifDoc, { read: true });
      } catch (error) {
        console.error("[useNotifications] markAsRead error:", error);
      }
    },
    [userId]
  );

  const markAllRead = useCallback(async () => {
    if (!userId || notifications.length === 0) return;
    try {
      const batch = writeBatch(db);
      notifications.forEach((notif) => {
        const notifDoc = doc(db, `users/${userId}/notifications`, notif.id);
        batch.update(notifDoc, { read: true });
      });
      await batch.commit();
    } catch (error) {
      console.error("[useNotifications] markAllRead error:", error);
    }
  }, [userId, notifications]);

  return { notifications, unreadCount, markAsRead, markAllRead };
}
